import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { OrganisationContext } from '../../../../context';
import { ChannelAppTabUpdateInput, organisationApi } from '../../../../services';
import {
  AppTabsSettings,
  Button,
  ButtonType,
  Check,
  IconLabel,
  InputField,
  LanguageByCode,
  LOCALES,
  normalizeSubdomain,
  RestrictDuplicateUrlOfCardsInTimeFrame,
  Select,
  useRestrictDuplicateUrlOfCardsInTimeFrame,
} from '../../../../shared';

import classes from './General.module.scss';

export const General: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardGeneral' });

  const [organisationUpdate] = organisationApi.endpoints.organisationUpdate.useMutation();

  const { organisation, setOrganisation } = useContext(OrganisationContext);

  const {
    name: orgName,
    subdomain: orgSubdomain,
    localeId: orgLocaleId,
    chatPromotionEnabled: orgChatPromotionEnabled,
    restrictDuplicateUrlOfCards: orgRestrictDuplicateUrlOfCards,
    appTabsSettings: { appTabs: orgAppTabs },
  } = organisation;

  const { options: restrictDuplicateUrlOfCardsInTimeFrameOptions } =
    useRestrictDuplicateUrlOfCardsInTimeFrame();

  const [name, setOrgName] = useState<string>(orgName);

  const [subdomain, setSubdomain] = useState<string>(orgSubdomain);

  const [localeId, setLocaleId] = useState<number>(orgLocaleId);

  const [lockUrl, setLockUrl] = useState<boolean>(true);

  const [chatPromotionEnabled, setChatPromotionEnabled] =
    useState<boolean>(orgChatPromotionEnabled);

  const [restrictDuplicateUrlOfCards, setRestrictDuplicateUrlOfCards] =
    useState<RestrictDuplicateUrlOfCardsInTimeFrame>(orgRestrictDuplicateUrlOfCards);

  const [appTabs, setAppTabs] = useState<ChannelAppTabUpdateInput[]>(
    orgAppTabs.map(({ action, defaultIconUrl, iconUrl, title }) => {
      return {
        action,
        iconUrl: { x1: iconUrl.x1 || defaultIconUrl.x1, x3: iconUrl.x3 || defaultIconUrl.x3 },
        title,
      };
    })
  );

  const [deepUpdate, setDeepUpdate] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const localeOptions = useMemo(
    () =>
      LOCALES.map(({ id, code }) => {
        return { id, title: LanguageByCode[code as keyof typeof LanguageByCode] };
      }),
    []
  );

  const onSave = useCallback(async () => {
    setLoading(true);

    try {
      const payload = await toast.promise(
        organisationUpdate({
          fields: {
            name,
            subdomain,
            localeId,
            localeIdDeepUpdate: deepUpdate,
            restrictDuplicateUrlOfCards,
            restrictDuplicateUrlOfCardsDeepUpdate: deepUpdate,
            chatPromotionEnabled,
            appTabs,
            appTabsDeepUpdate: deepUpdate,
          },
        }).unwrap(),
        { pending: t('orgDashboard.save-pending'), success: t('orgDashboard.save-success') }
      );

      if (payload) {
        setOrganisation?.({ ...organisation, ...payload });
        return;
      }
    } catch (_) {
      toast.error(t('orgDashboard.save-error'));
    } finally {
      setLoading(false);
    }
  }, [
    appTabs,
    chatPromotionEnabled,
    deepUpdate,
    localeId,
    name,
    organisation,
    organisationUpdate,
    restrictDuplicateUrlOfCards,
    setOrganisation,
    subdomain,
    t,
  ]);

  return (
    <div className={classes['general']}>
      <InputField
        label={ctxT('org-name')}
        value={name}
        onChange={({ target }) => setOrgName(target.value)}
      />

      <div className={classes['settings__subdomain-wrapper']}>
        <InputField
          label={t('common.url')}
          value={subdomain}
          onChange={({ target }) => setSubdomain(normalizeSubdomain(target.value))}
          disabled={lockUrl}
          icon={
            <IconLabel
              iconId={'lock'}
              iconSize={20}
              ariaLabel={t(`common.${lockUrl ? 'unlock' : 'lock'}`)}
              onClick={() => setLockUrl(!lockUrl)}
              singleColor
            />
          }
        />

        <div className={classes['general__subdomain-warning']}>{ctxT('change-url-warning')}</div>
      </div>

      <div className={classes['general__language-wrapper']}>
        <Select
          selectedItemId={localeId}
          label={t('common.language')}
          items={localeOptions}
          onChange={(localeId) => setLocaleId(localeId as number)}
        />

        <div className={classes['general__language-description']}>
          {ctxT('language-description')}
        </div>
      </div>

      <Check
        checked={chatPromotionEnabled}
        label={ctxT('show-chat-promo')}
        onChange={setChatPromotionEnabled}
      />

      <div className={classes['general__duplicates-wrapper']}>
        <Select
          label={ctxT('avoid-duplicates')}
          selectedItemId={restrictDuplicateUrlOfCards}
          items={restrictDuplicateUrlOfCardsInTimeFrameOptions}
          onChange={(restrictDuplicateUrlOfCards) =>
            setRestrictDuplicateUrlOfCards(
              restrictDuplicateUrlOfCards as RestrictDuplicateUrlOfCardsInTimeFrame
            )
          }
        />

        <div className={classes['general__duplicates-description']}>
          {ctxT('duplicates-description')}
        </div>
      </div>

      <AppTabsSettings
        appTabs={appTabs}
        setAppTabs={setAppTabs}
        langHintLocaliseKey={'appTabsSettings.org-lang-hint'}
        iconHintLocaliseKey={'appTabsSettings.org-icon-hint'}
      />

      <Check checked={deepUpdate} label={ctxT('deep-update')} onChange={setDeepUpdate} />

      <Button
        label={t('common.save')}
        type={ButtonType.primary}
        onClick={onSave}
        disabled={loading}
      />
    </div>
  );
};
